import type { CSSProperties } from 'react';
import type { CycleDesignerFormState, DesignerPath, OrgPathNode, PathSlotId, TrackParams } from '../types';
import { buildOrgById, describePath } from '../lib/pathBuilder';
import { buildCadenceSchedule, resolveResponseWindowDays } from '../lib/cadenceSchedule';

// 2026-07-08 owner ruling removed deadline/legitimacy/response-type/start-condition
// from this UI because nothing consumed them after launch. 2026-07-18 (T-minus
// cadence machinery, V2MOM): response windows returned — they are now consumed:
// they drive the cadence schedule (preview + track metadata + level config) and
// the first request's due date at launch. Legitimacy, response type, and start
// condition remain out of the UI; TrackParams keeps their defaults.

interface StepParametersProps {
  form: CycleDesignerFormState;
  paths: DesignerPath[];
  orgNodes: OrgPathNode[];
  onUpdateParam: (pathId: PathSlotId, key: keyof TrackParams, value: TrackParams[keyof TrackParams]) => void;
  onSetNodeWindow: (pathId: PathSlotId, nodeId: string, days: number | null) => void;
}

const numberInputStyle: CSSProperties = {
  width: 72,
  background: 'var(--bg)',
  border: '1px solid var(--rule-bright)',
  color: 'var(--text-pri)',
  padding: '6px 8px',
  fontSize: '0.9rem',
};

export function StepParameters({ form, paths, orgNodes, onUpdateParam, onSetNodeWindow }: StepParametersProps) {
  const orgById = buildOrgById(orgNodes);
  const schedule = buildCadenceSchedule(form, paths, orgNodes);

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {paths.map((path) => {
          const chain = describePath(path, orgById);
          const p = path.params;
          const cadence = schedule.paths.find((c) => c.pathId === path.id);
          return (
            <div key={path.id} style={{ border: '1px solid var(--rule-bright)', background: 'var(--bg-raised)', padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--rule)' }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: path.color }} />
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: 500, color: 'var(--text-pri)' }}>{path.name}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{chain} · {path.nodes.length} stops</div>
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 6 }}>
                  Response Guidance <span style={{ color: 'var(--text-dim)' }}>(optional)</span>
                </div>
                <textarea
                  rows={3}
                  value={p.guidance}
                  onChange={(e) => onUpdateParam(path.id, 'guidance', e.target.value)}
                  placeholder="What should a good proposal answer?"
                  style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--rule-bright)', color: 'var(--text-pri)', padding: '8px 10px', fontSize: '0.95rem', resize: 'vertical' }}
                />
              </div>

              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 6 }}>
                  Cadence — Response Windows
                </div>
                <p style={{ fontSize: '0.95rem', color: 'var(--text-ter)', margin: '0 0 10px', lineHeight: 1.45 }}>
                  Each level below the top gets a window; the calendar this creates shows on the review step.
                  {schedule.anchor === 'lock'
                    ? ' Counted back from the end date — the last level lands on T−0.'
                    : ' Counted forward from the start date.'}
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: '0.88rem', color: 'var(--text-sec)' }}>Default window</span>
                  <input
                    type="number"
                    min={1}
                    value={p.deadlineDays}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v > 0) onUpdateParam(path.id, 'deadlineDays', Math.round(v));
                    }}
                    style={numberInputStyle}
                  />
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-ter)' }}>days per level</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {path.nodes.map((nodeId, level) => {
                    if (level === 0) return null;
                    const override = path.nodeWindows?.[nodeId];
                    const effective = resolveResponseWindowDays(path, nodeId);
                    const stop = cadence?.stops.find((s) => s.nodeId === nodeId);
                    return (
                      <div
                        key={nodeId}
                        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) auto auto 1fr', alignItems: 'center', gap: 10, fontSize: '0.88rem' }}
                      >
                        <span style={{ color: 'var(--text-sec)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {orgById.get(nodeId)?.name ?? nodeId}
                        </span>
                        <input
                          type="number"
                          min={1}
                          placeholder={String(p.deadlineDays)}
                          value={override ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value.trim();
                            onSetNodeWindow(path.id, nodeId, raw === '' ? null : Number(raw));
                          }}
                          style={numberInputStyle}
                        />
                        <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                          {override ? `${effective}d override` : `inherits ${p.deadlineDays}d`}
                        </span>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', letterSpacing: '0.08em', color: 'var(--text-dim)', textAlign: 'right' }}>
                          {stop?.dueDate ?? stop?.relativeLabel ?? ''}
                          {stop?.tMinusLabel ? ` · ${stop.tMinusLabel}` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {cadence?.overrun && schedule.daysAvailable !== null && (
                  <div style={{ marginTop: 10, padding: '8px 10px', border: '1px solid var(--coral)', color: 'var(--coral)', fontSize: '0.95rem', lineHeight: 1.45 }}>
                    These windows total {cadence.totalDescentDays} days, but the cycle window is {schedule.daysAvailable} — tighten windows or move the dates.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
